const MOBILE_CAROUSEL_QUERY = '(max-width: 989px)';
const mobileCarouselMedia = window.matchMedia(MOBILE_CAROUSEL_QUERY);
const carouselTemplate = document.querySelector('#fastsport-mobile-carousel-controls');
const carouselScript = document.querySelector('script[data-fastsport-mobile-carousels]');
const carouselStates = new WeakMap();
let carouselId = 0;

const CAROUSELS = [
  {
    track: '.rv-grid',
    item: '.rv-card',
    label: 'Customer reviews',
  },
  {
    track: '.fb-grid',
    item: '.fb-card',
    label: 'Latest articles',
  },
];

const LABELS = {
  previous: carouselScript?.dataset.previousLabel || 'Previous slide',
  next: carouselScript?.dataset.nextLabel || 'Next slide',
  status: carouselScript?.dataset.statusTemplate || 'Slide __INDEX__ of __LENGTH__',
};

function formatSlideStatus(index, length) {
  return LABELS.status.replace('__INDEX__', String(index + 1)).replace('__LENGTH__', String(length));
}

/**
 * Applies carousel semantics only while the mobile layout is active.
 *
 * @param {HTMLElement} track
 * @param {Element[]} items
 * @param {{ item: string, label: string }} config
 */
function updateCarouselMode(track, items, config) {
  if (mobileCarouselMedia.matches) {
    track.tabIndex = 0;
    track.setAttribute('role', 'region');
    track.setAttribute('aria-roledescription', 'carousel');
    track.setAttribute('aria-label', config.label);
  } else {
    track.removeAttribute('tabindex');
    track.removeAttribute('role');
    track.removeAttribute('aria-roledescription');
    track.removeAttribute('aria-label');
    track.scrollTo({ left: 0, behavior: 'auto' });
  }

  items.forEach((item, index) => {
    const canAddSlideSemantics = !(item instanceof HTMLAnchorElement);

    if (mobileCarouselMedia.matches && canAddSlideSemantics) {
      item.dataset.mobileCarouselSemantics = 'true';
      item.setAttribute('role', 'group');
      item.setAttribute('aria-roledescription', 'slide');
      item.setAttribute('aria-label', formatSlideStatus(index, items.length));
    } else if (item.dataset.mobileCarouselSemantics === 'true') {
      delete item.dataset.mobileCarouselSemantics;
      item.removeAttribute('role');
      item.removeAttribute('aria-roledescription');
      item.removeAttribute('aria-label');
    }
  });
}

/**
 * Creates the visible arrow and progress controls from Shopify theme assets.
 *
 * @param {HTMLElement} track
 * @returns {{ previous: HTMLButtonElement, next: HTMLButtonElement, progress: HTMLElement, progressBar: HTMLElement, status: HTMLElement } | null}
 */
function createCarouselUi(track) {
  if (!(carouselTemplate instanceof HTMLTemplateElement) || !(track.parentElement instanceof HTMLElement)) return null;

  const fragment = carouselTemplate.content.cloneNode(true);
  const previous = fragment.querySelector('[data-carousel-previous]');
  const next = fragment.querySelector('[data-carousel-next]');
  const progress = fragment.querySelector('[data-carousel-progress]');
  const progressBar = fragment.querySelector('[data-carousel-progress-bar]');
  const status = fragment.querySelector('[data-carousel-status]');

  if (
    !(previous instanceof HTMLButtonElement) ||
    !(next instanceof HTMLButtonElement) ||
    !(progress instanceof HTMLElement) ||
    !(progressBar instanceof HTMLElement) ||
    !(status instanceof HTMLElement)
  ) {
    return null;
  }

  const shell = document.createElement('div');
  shell.className = 'fsmc-shell';
  track.id ||= `fastsport-mobile-carousel-${++carouselId}`;

  previous.setAttribute('aria-label', LABELS.previous);
  previous.setAttribute('aria-controls', track.id);
  next.setAttribute('aria-label', LABELS.next);
  next.setAttribute('aria-controls', track.id);

  track.parentElement.insertBefore(shell, track);
  shell.append(previous, track, next);
  shell.after(progress, status);

  return { previous, next, progress, progressBar, status };
}

/**
 * Returns the slide nearest the track's leading edge.
 *
 * @param {HTMLElement} track
 * @param {Element[]} items
 */
function getClosestIndex(track, items) {
  const trackStart = track.getBoundingClientRect().left;
  let closestIndex = 0;
  let closestDistance = Number.POSITIVE_INFINITY;

  items.forEach((item, index) => {
    const distance = Math.abs(item.getBoundingClientRect().left - trackStart);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = index;
    }
  });

  return closestIndex;
}

/**
 * Adds swipe, arrow, progress, and keyboard behavior to one existing card track.
 *
 * @param {HTMLElement} track
 * @param {{ item: string, label: string }} config
 */
function enhanceCarousel(track, config) {
  const items = [...track.querySelectorAll(config.item)];
  if (items.length < 2) return;

  updateCarouselMode(track, items, config);

  const existingState = carouselStates.get(track);
  if (existingState) {
    existingState.update();
    return;
  }

  const ui = createCarouselUi(track);
  if (!ui) return;

  let activeIndex = 0;
  let scrollFrame = 0;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const update = () => {
    activeIndex = getClosestIndex(track, items);
    ui.previous.disabled = activeIndex === 0;
    ui.next.disabled = activeIndex === items.length - 1;
    ui.progress.setAttribute('aria-valuemin', '1');
    ui.progress.setAttribute('aria-valuemax', String(items.length));
    ui.progress.setAttribute('aria-valuenow', String(activeIndex + 1));
    ui.progressBar.style.width = `${100 / items.length}%`;
    ui.progressBar.style.transform = `translateX(${activeIndex * 100}%)`;
    ui.status.textContent = formatSlideStatus(activeIndex, items.length);
  };

  const goTo = (index, behavior = reducedMotion.matches ? 'auto' : 'smooth') => {
    const targetIndex = Math.max(0, Math.min(items.length - 1, index));
    const targetLeft = items[targetIndex].offsetLeft - items[0].offsetLeft;
    track.scrollTo({ left: targetLeft, behavior });
  };

  ui.previous.addEventListener('click', () => goTo(activeIndex - 1));
  ui.next.addEventListener('click', () => goTo(activeIndex + 1));

  track.addEventListener('keydown', (event) => {
    if (!mobileCarouselMedia.matches) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    goTo(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
  });

  track.addEventListener(
    'scroll',
    () => {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(() => {
        scrollFrame = 0;
        update();
      });
    },
    { passive: true }
  );

  if ('ResizeObserver' in window) {
    const resizeObserver = new ResizeObserver(() => {
      if (mobileCarouselMedia.matches) goTo(activeIndex, 'auto');
      update();
    });
    resizeObserver.observe(track);
  }

  const state = { update };
  carouselStates.set(track, state);
  track.dataset.mobileCarouselReady = 'true';
  update();
}

/**
 * Enhances homepage carousels inside a document or a freshly loaded Shopify section.
 *
 * @param {Document | Element} root
 */
function initializeMobileCarousels(root = document) {
  CAROUSELS.forEach((config) => {
    root.querySelectorAll(config.track).forEach((track) => {
      if (track instanceof HTMLElement) enhanceCarousel(track, config);
    });
  });
}

initializeMobileCarousels();

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) initializeMobileCarousels(event.target);
});

mobileCarouselMedia.addEventListener('change', () => {
  initializeMobileCarousels();
});